<template>
  <UModal v-model:open="isOpen" :fullscreen='isMobile ? true : false' :dismissible="false"
    :ui="isMobile ? {} : { content: 'h-[95vh] max-w-11/12 flex flex-col', footer: 'md:h-15' }">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <h3 class="text-lg font-semibold">Setări confidențiale</h3>
      </div>
    </template>

    <template #body>
      <div class="flex flex-1 gap-6 min-h-0 h-full" :class="isMobile ? 'flex-col' : 'flex-row'">

        <div class="flex-col flex flex-shrink-0 space-y-4">
          <UTabs v-model="selectedTab" :items="tabItems" :orientation="isMobile ? 'horizontal' : 'vertical'"
            variant="link" color="primary" class="" />

          <div class="flex mt-auto">
            <CAOCookies variant="link" content-side="top" />
          </div>
        </div>
        
        <div class="flex-1 h-full flex flex-col gap-4">
          <div v-show="selectedTab === 'gdpr'" class="flex-1 min-h-0">
            <div ref="gdprContainer" class="h-full overflow-y-auto">
              <div ref="gdprPdfContainer" class="w-full"></div>
            </div>
          </div>


          <div v-show="selectedTab === 'terms'" class="flex-1 min-h-0">
            <div ref="termsContainer" class="h-full overflow-y-auto">
              <div ref="termsPdfContainer" class="w-full"></div>
            </div>
          </div>


          <div v-if="selectedTab === 'notifications' && !nav.isStandalone" class="h-full overflow-y-auto p-1">
            <ProfilSetari welcome />
          </div>


        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-col md:flex-row items-center justify-between w-full gap-4">
        <div class="flex items-center gap-4">
          <UButton v-if="selectedTab === 'gdpr'" size="xl" label="Continuă" color="primary"
            @click="acceptGdpr"
            class="w-full md:w-auto justify-center" />
          <UButton v-else-if="selectedTab === 'terms'" size="xl" label="Continuă" color="primary"
            @click="acceptTerms"
            class="w-full md:w-auto justify-center" />
        </div>
        <div class="flex items-center gap-2 text-sm text-muted-foreground">
          <span class="font-medium">{{ acceptedCount }}/{{ totalDocuments }}</span>
          <span>documente acceptate</span>
        </div>

        <div class="flex items-center w-full md:w-fit gap-4">
          <UButton v-if="gdrpAgreed && termsAgreed" size="xl" type="submit" label="Sunt de acord" color="primary"
            @click="approveAll" class="w-full justify-center" />
          <UButton size="xl" label="Refuză" color="error" variant="soft" @click="useSecurity().logout()"
            class="w-full justify-center" />
        </div>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { isProblem, isValidation } from '@kodall/kodall-client/dist/types'
import { key } from '~/service/kodall-client'
import type { TermsGdpr } from './Form/Acorduri.vue'
import * as pdfjsLib from "pdfjs-dist"
import workerSrc from "pdfjs-dist/build/pdf.worker.mjs?url"

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc

const nav = useNavigation()
const toast = useToast()
const account = useAccount();
const gdrpAgreed = ref(false)
const termsAgreed = ref(false)
const kc = useSecurity().kc.value
const isOpen = defineModel<boolean>({ default: false })
const selectedTab = ref('gdpr')

const files = ref<TermsGdpr>()
const gdprContainer = ref<HTMLElement | null>(null)
const termsContainer = ref<HTMLElement | null>(null)
const gdprPdfContainer = ref<HTMLElement | null>(null)
const termsPdfContainer = ref<HTMLElement | null>(null)

const loadedPdfs = ref({
  gdpr: false,
  terms: false
})

const totalDocuments = computed(() => 2)

const acceptedCount = computed(() => {
  let count = 0
  if (gdrpAgreed.value) count++
  if (termsAgreed.value) count++
  return count
})

onMounted(async () => {
  const result = await kc.workflow<TermsGdpr>('GetAppConfig', 'getTermsGdpr', {})
  if (isProblem(result) || isValidation(result)) return

  files.value = result
  gdrpAgreed.value = !!account.account.value!.gdpr
  termsAgreed.value = !!account.account.value!.terms_conditions
})

watch([selectedTab, files], async ([newTab]) => {
  if (newTab === 'gdpr' && !loadedPdfs.value.gdpr && files.value?.id_template_gdpr) {
    await nextTick()
    await loadPdf('gdpr')
  } else if (newTab === 'terms' && !loadedPdfs.value.terms && files.value?.id_template_termeni_conditii) {
    await nextTick()
    await loadPdf('terms')
  }
}, { immediate: true })

const loadPdf = async (type: 'gdpr' | 'terms') => {
  const container = type === 'gdpr' ? gdprPdfContainer.value : termsPdfContainer.value
  const fileId = type === 'gdpr' ? files.value?.id_template_gdpr : files.value?.id_template_termeni_conditii
  
  if (!container || !fileId || loadedPdfs.value[type]) return
  
  try {
    const url = `/storage?id=${fileId}&action=view`
    const loadingTask = pdfjsLib.getDocument(url)
    const pdf = await loadingTask.promise
    
    container.innerHTML = ''
    
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum)
      const viewport = page.getViewport({ scale: 1.5 })
      
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.height = viewport.height
      canvas.width = viewport.width
      canvas.className = 'mb-4 mx-auto shadow-lg'
      
      container.appendChild(canvas)
      
      if (context) {
        await page.render({
          canvas,
          canvasContext: context,
          viewport: viewport
        }).promise
      }
    }
    
    loadedPdfs.value[type] = true
  } catch (error) {
    console.error('Error loading PDF:', error)
    toast.add({ title: 'Eroare', description: 'PDF-ul nu a putut fi încărcat', color: 'error' })
  }
}

const tabItems = computed(() => [
  {
    label: 'GDPR',
    value: 'gdpr',
    icon: 'i-lucide-shield-check'
  },
  {
    label: 'Termeni & condiții',
    value: 'terms',
    icon: 'i-lucide-file-text'
  },
  ...(!nav.isStandalone ? [{
    label: 'Notificări',
    value: 'notifications',
    icon: 'i-lucide-bell'
  }] : [])
])

const acceptGdpr = () => {
  gdrpAgreed.value = true
  goToNextTab()
}

const acceptTerms = () => {
  termsAgreed.value = true
  goToNextTab()
}

const goToNextTab = () => {
  const currentIndex = tabItems.value.findIndex(item => item.value === selectedTab.value)
  if (!tabItems.value || currentIndex + 1 >= tabItems.value.length) return
  const nextTab = tabItems.value[currentIndex + 1]
  if (nextTab) {
    selectedTab.value = nextTab.value
  }
}

const emit = defineEmits<{
  approveAll: []
}>()

const approveAll = async () => {
  emit('approveAll')
  var result = await account.updateAccount({ ...account.account.value!, gdpr: true, terms_conditions: true })
  if (isProblem(result) || isValidation(result)) {
    toast.add({ title: "Eroare", description: result.detail, color: "error" })
  }
  isOpen.value = false
}
</script>